import { EntityLabel, Role } from '@esg/esg-global-types';
import { DocumentReference } from 'firebase/firestore';
import { apollo_client } from '../app/db_util';
import { generateUuidV4 } from '../../util/uuid';
import { MetadataError } from '@ep/error-handling';
import { GET_ROLES_QUERY } from '../graphql/queries/user_management/role';
import {
  CREATE_ROLE_MUTATOR,
  DELETE_ROLE_MUTATOR,
  UPDATE_ROLE_MUTATOR
} from '../graphql/mutations/user_management/role';
export type RoleData = Omit<Role, 'id'>;

// Singular and plural label for model entity.
export const role_label: EntityLabel = {
  one: 'Role',
  many: 'Roles'
};

/**
 * Query all role documents for master list
 * @param {string} id Optional query for a specific role document
 * @returns {Promise<Array<Role>>}
 */
export const getRoles = async (id?: string): Promise<Array<Role>> => {
  try {
    const roles: Array<Role> = await apollo_client
      .query({
        variables: {
          id: id
        },
        query: GET_ROLES_QUERY,
        fetchPolicy: 'no-cache'
      })
      .then((response) => {
        return response.data.roles;
      });
    return roles;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error ? err.message : 'Error: getRoles failed on an unknown error.',
      {
        id: id
      },
      tracking_id
    );
  }
};

/**
 * Create Role with relative data
 * @param {RoleData} role Data of new Role to add to doc
 * @returns {DocumentReference}
 */
export const createRole = async (role: RoleData): Promise<DocumentReference> => {
  try {
    const role_doc: DocumentReference = await apollo_client
      .mutate({
        mutation: CREATE_ROLE_MUTATOR,
        variables: {
          role: role
        }
      })
      .then((response) => {
        return response.data.createRole;
      });
    return role_doc;
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/role.ts failed on an unknown error while calling createRole.',
      {
        role: role
      },
      tracking_id
    );
  }
};

/**
 * Update Role with relative data
 * @param {Role} updated_role New role data to push into document
 * @returns {void}
 */
export const updateRole = async (updated_role: Role): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: UPDATE_ROLE_MUTATOR,
        variables: {
          updated_role: updated_role
        }
      })
      .then((response) => {
        return response.data.updateRole;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/role.ts failed on an unknown error while calling updateRole.',
      {
        updated_role: updated_role
      },
      tracking_id
    );
  }
};

/**
 * Soft delete a Role.
 * @param {string} role_id ID of role to delete
 * @returns {Promise<void>}
 */
export const deleteRole = async (role_id: string): Promise<void> => {
  try {
    await apollo_client
      .mutate({
        mutation: DELETE_ROLE_MUTATOR,
        variables: {
          role_id: role_id
        }
      })
      .then((response) => {
        return response.data.updateRole;
      });
  } catch (err: unknown) {
    const tracking_id: string = generateUuidV4();
    throw new MetadataError(
      err instanceof Error
        ? err.message
        : 'Error: lib/metric_capture/role.ts failed on an unknown error while calling deleteRole.',
      {
        role_id: role_id
      },
      tracking_id
    );
  }
};
